import { useState, type FormEvent } from "react";
import { fetchAuditLog, type AuditEntry, RequestError } from "../../api/client.ts";

const OUTCOME_COLOURS: Record<string, string> = {
  ok: "text-green-400",
  not_on_picking: "text-yellow-400",
  variance: "text-yellow-400",
  unknown: "text-orange-400",
  error: "text-red-400",
};

export function AdminAudit() {
  const [token, setToken] = useState("");
  const [pickingRef, setPickingRef] = useState("");
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token || !pickingRef) return;
    setLoading(true);
    setError(null);
    setEntries(null);
    try {
      const data = await fetchAuditLog(
        import.meta.env.VITE_API_BASE_URL as string,
        token,
        pickingRef,
      );
      setEntries(data);
    } catch (err) {
      if (err instanceof RequestError) {
        setError(`${err.status}: ${err.message}`);
      } else {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 p-6 text-white">
      <h1 className="mb-6 text-xl font-bold text-gray-100">Scan Audit Log</h1>

      <form onSubmit={(e) => void handleSubmit(e)} className="mb-6 flex flex-wrap gap-3">
        <input
          type="password"
          placeholder="Admin token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoComplete="off"
        />
        <input
          type="text"
          placeholder="Picking ref (e.g. PMWH/IN/00009)"
          value={pickingRef}
          onChange={(e) => setPickingRef(e.target.value)}
          className="w-64 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={loading || !token || !pickingRef}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Look up"}
        </button>
      </form>

      {error && (
        <div className="mb-4 rounded border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {entries !== null && (
        <>
          <p className="mb-3 text-sm text-gray-400">
            {entries.length} scan{entries.length !== 1 ? "s" : ""} for{" "}
            <span className="font-mono text-gray-200">{pickingRef}</span>
          </p>
          {entries.length === 0 ? (
            <p className="text-sm text-gray-500">No scans recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400">
                    <th className="pb-2 pr-4">Time</th>
                    <th className="pb-2 pr-4">Outcome</th>
                    <th className="pb-2 pr-4">Rule</th>
                    <th className="pb-2 pr-4">Batch</th>
                    <th className="pb-2 pr-4">Odoo TX</th>
                    <th className="pb-2">Exception</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b border-gray-900 hover:bg-gray-900/40">
                      <td className="py-2 pr-4 font-mono text-xs text-gray-400">
                        {new Date(e.createdAt).toLocaleTimeString()}
                      </td>
                      <td className={`py-2 pr-4 font-medium ${OUTCOME_COLOURS[e.outcome] ?? "text-gray-300"}`}>
                        {e.outcome}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-gray-400">{e.ruleId ?? "—"}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-gray-300">
                        {(e.normalised as { batch?: string } | null)?.batch ?? "—"}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-gray-400">{e.odooTxRef ?? "—"}</td>
                      <td className="py-2 text-xs text-red-400">{e.exception ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
